import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Chip,
  Typography,
  FormGroup,
  FormControl,
  FormLabel,
  Grid,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Apps as AppsIcon,
} from "@mui/icons-material";
import { SxProps, Theme } from "@mui/material/styles";
import {
  MenuPermission,
  getAllMenuItems,
  createDefaultPermission,
  supportsCRUD,
} from "../utils/menuPermissions";
import { dashboardBoxes } from "../utils/dashboardBoxes";
import { toast } from "react-toastify";

const styles: Record<string, SxProps<Theme>> = {
  searchField: {
    mb: 2,
  },
  accordion: {
    mb: 1,
    "&:before": {
      display: "none",
    },
  },
  accordionSummary: {
    bgcolor: "grey.50",
    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
    minHeight: 56,
    "&.Mui-expanded": {
      minHeight: 56,
    },
  },
  accordionDetails: {
    p: 2,
    flexDirection: "column",
  },
  menuItem: {
    display: "flex",
    alignItems: "center",
    p: 1.5,
    mb: 1,
    borderRadius: 1,
    border: (theme) => `1px solid ${theme.palette.divider}`,
    bgcolor: "background.paper",
    "&:hover": {
      bgcolor: "action.hover",
    },
  },
  menuItemSelected: {
    bgcolor: "action.selected",
    borderColor: "primary.main",
  },
  menuLabel: {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    flex: 1,
  },
  menuIcon: {
    fontSize: 20,
    color: "text.secondary",
  },
  menuText: {
    fontWeight: 500,
  },
  badgeContainer: {
    display: "flex",
    gap: 1,
    ml: 2,
  },
  badge: {
    fontSize: "0.75rem",
    height: 20,
  },
  badgeSuperadmin: {
    fontSize: "0.75rem",
    height: 20,
    borderColor: "#9c27b0",
    color: "#9c27b0",
    fontWeight: 600,
  },
  badgeAdmin: {
    fontSize: "0.75rem",
    height: 20,
    borderColor: "#2196f3",
    color: "#2196f3",
    fontWeight: 600,
  },
  badgeChatbot: {
    fontSize: "0.75rem",
    height: 20,
    borderColor: "#ff9800",
    color: "#ff9800",
    fontWeight: 600,
  },
  badgeUser: {
    fontSize: "0.75rem",
    height: 20,
    borderColor: "#757575",
    color: "#757575",
    fontWeight: 600,
  },
  badgeViewOnly: {
    fontSize: "0.65rem",
    height: 20,
    borderColor: "warning.main",
    color: "warning.main",
    fontWeight: 600,
    textTransform: "uppercase",
  },
  crudContainer: {
    mt: 1,
    ml: 5,
    p: 2,
    bgcolor: "grey.50",
    borderRadius: 1,
  },
  crudLabel: {
    mb: 1,
    fontWeight: 500,
    fontSize: "0.875rem",
  },
  crudCheckboxes: {
    display: "flex",
    gap: 2,
  },
  categoryTitle: {
    display: "flex",
    alignItems: "center",
    gap: 1,
  },
  categoryIcon: {
    fontSize: 20,
  },
  viewOnlyHelper: {
    mb: 2,
    color: "text.secondary",
  },
};

// Helper function to get badge sx based on role name
const getRoleBadgeSx = (roleName: string): SxProps<Theme> => {
  const normalizedName = roleName.toLowerCase();

  if (normalizedName === "superadmin") {
    return styles.badgeSuperadmin;
  } else if (normalizedName === "admin") {
    return styles.badgeAdmin;
  } else if (normalizedName === "chatbot") {
    return styles.badgeChatbot;
  } else if (normalizedName === "user") {
    return styles.badgeUser;
  }

  return styles.badge;
};

interface PermissionSelectorMUIProps {
  permissions: MenuPermission[];
  onChange: (permissions: MenuPermission[]) => void;
  disabled?: boolean;
}

export const PermissionSelectorMUI: React.FC<PermissionSelectorMUIProps> = ({
  permissions,
  onChange,
  disabled = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedPanels, setExpandedPanels] = useState<string[]>([]);

  const allMenuItems = getAllMenuItems();
  const dashboardLinkedPaths = React.useMemo(() => {
    const map = new Map<string, string[]>();
    dashboardBoxes.forEach((box) => {
      if (box.linkedMenuPaths && box.linkedMenuPaths.length) {
        map.set(`/dashboard/${box.id}`, box.linkedMenuPaths);
      }
    });
    return map;
  }, []);

  // Separate Dashboard items, root items, and grouped items
  const dashboardBoxItems = allMenuItems.filter(item => item.parentText === "Dashboard");
  const dashboardRootItem = allMenuItems.find(item => item.path === "/dashboard");
  const otherRootItems = allMenuItems.filter(item => !item.parentText && item.path !== "/dashboard");
  const otherGroupedItems = allMenuItems.filter(item => item.parentText && item.parentText !== "Dashboard");

  // Group non-dashboard child menu items by parent
  const otherGroupedMenuItems = otherGroupedItems.reduce((acc, item) => {
    const parent = item.parentText!;
    if (!acc[parent]) {
      acc[parent] = [];
    }
    acc[parent].push(item);
    return acc;
  }, {} as Record<string, typeof allMenuItems>);

  // Filter menu items based on search
  const filteredOtherRootItems = otherRootItems.filter(
    (item) =>
      item.text.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.path.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredDashboardBoxes = dashboardBoxItems.filter(
    (item) =>
      item.text.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.path.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredOtherGroups = Object.entries(otherGroupedMenuItems).reduce(
    (acc, [parent, items]) => {
      const filteredItems = items.filter(
        (item) =>
          item.text.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.path.toLowerCase().includes(searchTerm.toLowerCase())
      );
      if (filteredItems.length > 0) {
        acc[parent] = filteredItems;
      }
      return acc;
    },
    {} as Record<string, typeof allMenuItems>
  );

  // Expand all panels when searching
  useEffect(() => {
    if (searchTerm) {
      const panelsToExpand = [];
      if (filteredDashboardBoxes.length > 0) {
        panelsToExpand.push("Dashboard");
      }
      panelsToExpand.push(...Object.keys(filteredOtherGroups));
      setExpandedPanels(panelsToExpand);
    }
  }, [searchTerm, filteredDashboardBoxes, filteredOtherGroups]);

  const handlePanelChange = (panel: string) => (
    event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpandedPanels((prev) =>
      isExpanded ? [...prev, panel] : prev.filter((p) => p !== panel)
    );
  };

  const isMenuSelected = (path: string): boolean => {
    return permissions.some((p) => p.menuPath === path);
  };

  const getPermission = (path: string): MenuPermission | undefined => {
    return permissions.find((p) => p.menuPath === path);
  };
  const findMenuText = (menuPath: string): string => {
    const match = allMenuItems.find((m) => m.path === menuPath);
    return match?.text || menuPath;
  };

  /**
   * Sync rules:
   * - If a dashboard widget is selected, ensure linked pages have at least view permission.
   * - If a linked page has view permission, ensure its widget is selected.
   * - If a permission has all CRUD flags false, drop the permission (uncheck main item).
   */
  const applySyncRules = (currentPermissions: MenuPermission[]): MenuPermission[] => {
    let updated = [...currentPermissions];

    const getPerm = (path: string) => updated.find((p) => p.menuPath === path);
    const addOrEnsureView = (path: string) => {
      const existing = getPerm(path);
      if (existing) {
        if (!existing.canView) {
          updated = updated.map((p) =>
            p.menuPath === path ? { ...p, canView: true } : p
          );
        }
        return;
      }
      updated.push(createDefaultPermission(path, findMenuText(path)));
    };

    // Drop permissions that have all CRUD flags off (including view)
    updated = updated.filter((p) => p.canView || p.canCreate || p.canEdit || p.canDelete);

    // Two-way sync between dashboard widgets and their linked pages
    dashboardLinkedPaths.forEach((linkedPaths, widgetPath) => {
      const widgetSelected = !!getPerm(widgetPath);
      const linkedHasView = linkedPaths.some((lp) => getPerm(lp)?.canView);

      // If no linked page has view, drop the widget (honor user uncheck)
      if (widgetSelected && !linkedHasView) {
        updated = updated.filter((p) => p.menuPath !== widgetPath);
        return;
      }

      // If widget is selected and linked pages have view, ensure view on them
      if (widgetSelected && linkedHasView) {
        linkedPaths.forEach(addOrEnsureView);
      }

      // If linked page gets view, ensure widget exists
      if (linkedHasView && !widgetSelected) {
        addOrEnsureView(widgetPath);
      }
    });

    return updated;
  };

  const ensureLinkedMenusSelected = (paths: string[]) => {
    if (disabled) return;
    let updated = [...permissions];
    let added = false;

    paths.forEach((path) => {
      const exists = updated.some((p) => p.menuPath === path);
      if (!exists) {
        updated.push(createDefaultPermission(path, findMenuText(path)));
        added = true;
      }
    });
    if (!added) {
      toast.info("Seite ist bereits in den Berechtigungen enthalten.");
      return;
    }

    onChange(updated);
    toast.success("Seiten-Berechtigung hinzugefügt.");
  };

  const handleToggleMenu = (path: string, text: string) => {
    if (disabled) return;

    const existingPermission = getPermission(path);

    let updated: MenuPermission[];

    if (existingPermission) {
      // Remove permission
      updated = permissions.filter((p) => p.menuPath !== path);

      // If it's a dashboard widget, also remove linked page permissions to honor the uncheck
      if (path.startsWith("/dashboard/")) {
        const linked = dashboardLinkedPaths.get(path) || [];
        if (linked.length) {
          updated = updated.filter((p) => !linked.includes(p.menuPath));
        }
      }
    } else {
      // Add permission with default values
      const newPermission = createDefaultPermission(path, text);
      updated = [...permissions, newPermission];
    }

    onChange(applySyncRules(updated));
  };

  const handleCRUDChange = (path: string, operation: "canView" | "canCreate" | "canEdit" | "canDelete") => {
    if (disabled) return;

    const toggledPermissions = permissions.map((p) => {
      if (p.menuPath === path) {
        return { ...p, [operation]: !p[operation] };
      }
      return p;
    });

    const permAfterToggle = toggledPermissions.find((p) => p.menuPath === path);

    // If canView was turned off, remove the permission completely.
    if (permAfterToggle && operation === "canView" && !permAfterToggle.canView) {
      const cleaned = toggledPermissions.filter((p) => p.menuPath !== path);
      onChange(applySyncRules(cleaned));
      return;
    }

    // If all flags are false, remove it
    const cleaned =
      permAfterToggle &&
      !permAfterToggle.canView &&
      !permAfterToggle.canCreate &&
      !permAfterToggle.canEdit &&
      !permAfterToggle.canDelete
        ? toggledPermissions.filter((p) => p.menuPath !== path)
        : toggledPermissions;

    onChange(applySyncRules(cleaned));
  };

  const getCategoryIcon = (category: string) => {
    const iconMap: Record<string, React.ReactElement> = {
      "Hauptmenü": <DashboardIcon sx={styles.categoryIcon} />,
      "IMPACT ECK": <AppsIcon sx={styles.categoryIcon} />,
      "COMMUNITY": <PeopleIcon sx={styles.categoryIcon} />,
      "DEIN KEB": <AppsIcon sx={styles.categoryIcon} />,
      "EINSTELLUNGEN": <SettingsIcon sx={styles.categoryIcon} />,
      "ANDERE": <AppsIcon sx={styles.categoryIcon} />,
    };
    return iconMap[category] || <AppsIcon sx={styles.categoryIcon} />;
  };

  const getExistingRoles = (path: string): string[] => {
    // Roles are no longer used - permissions determine access
    return [];
  };

  return (
    <Box>
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Menüpunkte durchsuchen..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        disabled={disabled}
        sx={styles.searchField}
        size="small"
      />

      {/* Render other root menu items directly without grouping */}
      {filteredOtherRootItems.map((item) => {
        const isSelected = isMenuSelected(item.path);
        const permission = getPermission(item.path);
        const hasCRUD = supportsCRUD(item.path);
        const existingRoles = getExistingRoles(item.path);

        return (
          <Box key={item.path}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                p: 1.5,
                mb: 1,
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                bgcolor: isSelected ? "action.selected" : "background.paper",
                borderColor: isSelected ? "primary.main" : undefined,
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isSelected}
                    onChange={() => handleToggleMenu(item.path, item.text)}
                    disabled={disabled}
                    color="primary"
                  />
                }
                label={
                  <Box sx={styles.menuLabel}>
                    <DashboardIcon sx={styles.menuIcon} />
                    <Typography sx={styles.menuText}>
                      {item.text}
                    </Typography>
                    {existingRoles.length > 0 && (
                      <Box sx={styles.badgeContainer}>
                        {existingRoles.map((role) => (
                          <Chip
                            key={role}
                            label={role}
                            size="small"
                            sx={getRoleBadgeSx(role)}
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                }
              />
            </Box>

            {isSelected && hasCRUD && permission && (
              <Box sx={styles.crudContainer}>
                <Typography sx={styles.crudLabel}>
                  CRUD-Berechtigungen
                </Typography>
                <FormGroup sx={styles.crudCheckboxes}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={permission.canCreate}
                        onChange={() => handleCRUDChange(item.path, "canCreate")}
                        disabled={disabled}
                        color="primary"
                        size="small"
                      />
                    }
                    label="Erstellen"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={permission.canView}
                        onChange={() => handleCRUDChange(item.path, "canView")}
                        disabled={disabled}
                        color="primary"
                        size="small"
                      />
                    }
                    label="Ansehen"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={permission.canEdit}
                        onChange={() => handleCRUDChange(item.path, "canEdit")}
                        disabled={disabled}
                        color="primary"
                        size="small"
                      />
                    }
                    label="Bearbeiten"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={permission.canDelete}
                        onChange={() => handleCRUDChange(item.path, "canDelete")}
                        disabled={disabled}
                        color="primary"
                        size="small"
                      />
                    }
                    label="Löschen"
                  />
                </FormGroup>
              </Box>
            )}
          </Box>
        );
      })}

      {/* Render Dashboard accordion with boxes */}
      {dashboardRootItem && (
        <Accordion
          key="Dashboard"
          expanded={expandedPanels.includes("Dashboard")}
          onChange={handlePanelChange("Dashboard")}
          sx={styles.accordion}
          elevation={0}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={styles.accordionSummary}
          >
            <Box sx={styles.categoryTitle}>
              {getCategoryIcon("Dashboard")}
              <Typography variant="subtitle2">{dashboardRootItem.text}</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={styles.accordionDetails}>
            <>
              <Typography variant="body2" sx={styles.viewOnlyHelper}>
                Dashboard-Widgets sind rein für die Ansicht gedacht.<br />
                Die Berechtigung steuert nur, ob das Widget sichtbar ist – zusätzliche
                CRUD-Rechte sind hier nicht nötig.
              </Typography>
              {/* Dashboard box items */}
              {filteredDashboardBoxes.map((item) => {
                const isSelected = isMenuSelected(item.path);
                const existingRoles = getExistingRoles(item.path);
                const dashboardLabel = `Dashboard · ${item.text} (Widget)`;
                const linkedPaths = dashboardLinkedPaths.get(item.path) || [];

                return (
                  <Box key={item.path} style={{ width: "100%", marginTop: "12px" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 1.5,
                        mb: 1,
                        borderRadius: 1,
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        bgcolor: isSelected ? "action.selected" : "background.paper",
                        borderColor: isSelected ? "primary.main" : undefined,
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleToggleMenu(item.path, dashboardLabel)}
                            disabled={disabled}
                            color="primary"
                          />
                        }
                        label={
                          <Box sx={styles.menuLabel}>
                            <DashboardIcon sx={styles.menuIcon} />
                            <Typography sx={styles.menuText}>
                              {dashboardLabel}
                            </Typography>
                            <Box sx={styles.badgeContainer}>
                              <Chip
                                label="Nur Ansicht"
                                size="small"
                                sx={styles.badgeViewOnly}
                                variant="outlined"
                              />
                              {linkedPaths.map((lp) => (
                                <Chip
                                  key={lp}
                                  label={`Seite: ${lp}`}
                                  size="small"
                                  sx={styles.badge}
                                  variant="outlined"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    ensureLinkedMenusSelected([lp]);
                                  }}
                                  disabled={disabled}
                                />
                              ))}
                              {existingRoles.length > 0 &&
                                existingRoles.map((role) => (
                                  <Chip
                                    key={role}
                                    label={role}
                                    size="small"
                                    sx={getRoleBadgeSx(role)}
                                    variant="outlined"
                                  />
                                ))}
                            </Box>
                          </Box>
                        }
                      />
                    </Box>
                    {/* CRUD controls intentionally omitted for Dashboard widgets */}
                  </Box>
                );
              })}
            </>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Render other grouped items in accordions */}
      {Object.entries(filteredOtherGroups).map(([parent, items]) => (
        <Accordion
          key={parent}
          expanded={expandedPanels.includes(parent)}
          onChange={handlePanelChange(parent)}
          sx={styles.accordion}
          elevation={0}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={styles.accordionSummary}
          >
            <Box sx={styles.categoryTitle}>
              {getCategoryIcon(parent)}
              <Typography variant="subtitle2">{parent}</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={styles.accordionDetails}>
            {items.map((item) => {
              const isSelected = isMenuSelected(item.path);
              const permission = getPermission(item.path);
              const hasCRUD = supportsCRUD(item.path);
              const existingRoles = getExistingRoles(item.path);

              return (
                <Box key={item.path}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 1.5,
                      mb: 1,
                      borderRadius: 1,
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                      bgcolor: isSelected ? "action.selected" : "background.paper",
                      borderColor: isSelected ? "primary.main" : undefined,
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleToggleMenu(item.path, item.text)}
                          disabled={disabled}
                          color="primary"
                        />
                      }
                      label={
                        <Box sx={styles.menuLabel}>
                          <DashboardIcon sx={styles.menuIcon} />
                          <Typography sx={styles.menuText}>
                            {item.text}
                          </Typography>
                          {existingRoles.length > 0 && (
                            <Box sx={styles.badgeContainer}>
                              {existingRoles.map((role) => (
                                <Chip
                                  key={role}
                                  label={role}
                                  size="small"
                                  sx={getRoleBadgeSx(role)}
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </Box>

                  {isSelected && hasCRUD && permission && !item.path.startsWith("/dashboard/") && (
                    <Box sx={styles.crudContainer}>
                      <Typography sx={styles.crudLabel}>
                        CRUD-Berechtigungen
                      </Typography>
                      <FormGroup sx={styles.crudCheckboxes}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={permission.canCreate}
                              onChange={() => handleCRUDChange(item.path, "canCreate")}
                              disabled={disabled}
                              color="primary"
                              size="small"
                            />
                          }
                          label="Erstellen"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={permission.canView}
                              onChange={() => handleCRUDChange(item.path, "canView")}
                              disabled={disabled}
                              color="primary"
                              size="small"
                            />
                          }
                          label="Ansehen"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={permission.canEdit}
                              onChange={() => handleCRUDChange(item.path, "canEdit")}
                              disabled={disabled}
                              color="primary"
                              size="small"
                            />
                          }
                          label="Bearbeiten"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={permission.canDelete}
                              onChange={() => handleCRUDChange(item.path, "canDelete")}
                              disabled={disabled}
                              color="primary"
                              size="small"
                            />
                          }
                          label="Löschen"
                        />
                      </FormGroup>
                    </Box>
                  )}
                </Box>
              );
            })}
          </AccordionDetails>
        </Accordion>
      ))}

      {filteredOtherRootItems.length === 0 && filteredDashboardBoxes.length === 0 && Object.keys(filteredOtherGroups).length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography variant="body2" color="textSecondary">
            Keine Menüpunkte gefunden
          </Typography>
        </Box>
      )}
    </Box>
  );
};
